import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, ChevronRight, ChevronDown, ChevronLeft, Radio, X, Play, ExternalLink, Link2 } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend
} from 'recharts';
import { useAuth } from '../../context/AuthContext';

/**
 * Canon · Coral Campaign Report — /r/canon-coral  (English UI by request)
 *
 * Backend (src/routes/canonReport.js):
 *   GET /api/canon-report/coral?since=&until=   live Meta Graph API, no DB snapshot
 *   GET /api/canon-report/coral/ad/:adId        creative detail for the lightbox
 *       (fresh video source on demand, carousel cards, Meta preview iframes)
 *
 * Results = landing page views (optimisation goal). Total reach is the
 * account-level deduplicated value, not a row sum.
 * Canon only (App.js allowBrandIds + Sidebar restrictedBrands + backend brand access check).
 */

const CANON_BRAND_ID = '3987361f-4006-4819-bd83-f78b5a2155ec';
const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', orange: '#eb6834',
  good: '#047857', goodSoft: '#ECFDF5', bad: '#B91C1C', badSoft: '#FEF2F2', warnSoft: '#FFFBEB', warn: '#92400E',
};
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const nf = (n, d = 0) => (n == null || !Number.isFinite(Number(n)) ? '—'
  : Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }));
const tl = (n, d = 2) => (n == null || !Number.isFinite(Number(n)) ? '—' : `TRY ${nf(n, d)}`);
const pc = (n, d = 1) => (n == null ? '—' : `${nf(n, d)}%`);
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const day = (iso) => (iso ? `${MON[Number(iso.slice(5, 7)) - 1]} ${Number(iso.slice(8, 10))}` : '—');
const TZ = 'Europe/Istanbul';
const isoTZ = (date) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(date);
const format = (name = '') => (/carousel/i.test(name) ? 'Carousel' : /video/i.test(name) ? 'Video' : 'Other');
const wrap = { whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' };

function statusOf(c) {
  if (c.stopTime && new Date(c.stopTime).getTime() < Date.now()) return { label: 'Ended', bg: C.hair, fg: C.muted };
  if (c.status === 'ACTIVE') return { label: 'Active', bg: C.goodSoft, fg: C.good };
  if (/PAUSED/.test(c.status || '')) return { label: 'Paused', bg: C.warnSoft, fg: C.warn };
  return { label: c.status || '—', bg: C.hair, fg: C.muted };
}

// Flags on a destination URL — things worth a second look in the tracking setup.
function urlFlags(ad) {
  const d = ad.destination || {};
  const flags = [];
  const final = d.finalUrl || d.url || '';
  if (/%E2%80%8B|​/i.test(final)) flags.push('Hidden zero-width character in URL');
  if (/fbclid=fbclid/.test(final)) flags.push('Literal "fbclid=fbclid" in URL');
  const content = d.utm?.utm_content || '';
  if (ad.creativeType === 'video' && /_crsl_/.test(content)) flags.push('Video ad tagged as carousel (utm_content)');
  if (ad.creativeType === 'carousel' && /_vd_/.test(content)) flags.push('Carousel ad tagged as video (utm_content)');
  return flags;
}

const PRESETS = [
  { key: 'lifetime', label: 'Campaign lifetime' },
  { key: 'sep', label: 'Sep 1–30' },
  { key: 'oct', label: 'October' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'custom', label: 'Custom' },
];
function presetRange(key) {
  const today = isoTZ(new Date());
  if (key === 'sep') return ['2026-09-01', '2026-09-30'];
  if (key === 'oct') return ['2026-10-01', today];
  if (key === 'last7') {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return [isoTZ(d), today];
  }
  return null; // lifetime → backend: earliest start → today
}

/* ---------------------------------------------------------------- pieces */

const Card = ({ children, style }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
    boxShadow: '0 1px 2px rgba(16,24,40,0.04)', ...style,
  }}>{children}</div>
);

function Kpi({ label, value, sub, accent }) {
  return (
    <Card style={{ padding: '14px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: accent ? C.accent : C.ink, letterSpacing: '-0.02em', lineHeight: 1.15, whiteSpace: 'nowrap' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>{sub}</div>}
    </Card>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ padding: '10px 14px', borderRight: `1px solid ${C.hair}`, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.ink2 }}>{value}</div>
    </div>
  );
}

const Badge = ({ d }) => (
  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: d.bg, color: d.fg, whiteSpace: 'nowrap' }}>{d.label}</span>
);

function RateBar({ value }) {
  if (value == null) return <span>—</span>;
  const w = Math.max(0, Math.min(100, value));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
      <div style={{ width: 36, height: 6, background: C.hair, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${w}%`, height: '100%', background: C.accent, borderRadius: 3 }} />
      </div>
      <span style={{ minWidth: 42, textAlign: 'right' }}>{pc(value)}</span>
    </div>
  );
}

const COLS = [
  { key: 'spend', label: 'Spend', fmt: (v) => tl(v) },
  { key: 'impressions', label: 'Impressions', fmt: (v) => nf(v) },
  { key: 'reach', label: 'Reach', fmt: (v) => nf(v) },
  { key: 'frequency', label: 'Frequency', fmt: (v) => nf(v, 2) },
  { key: 'linkClicks', label: 'Link clicks', fmt: (v) => nf(v) },
  { key: 'ctr', label: 'CTR (link)', fmt: (v) => pc(v, 2) },
  { key: 'cpc', label: 'CPC (link)', fmt: (v) => tl(v) },
  { key: 'results', label: 'Results (LPV)', fmt: (v) => nf(v), strong: true },
  { key: 'costPerLpv', label: 'Cost / LPV', fmt: (v) => tl(v), strong: true },
  { key: 'lpvRate', label: 'LPV / click', bar: true },
  { key: 'thruplay', label: 'ThruPlays', fmt: (v) => (v ? nf(v) : '—') },
];
// Reach/frequency per ad equal the campaign row (1 ad per campaign) — kept in the campaign table.
const AD_COLS = COLS.filter((c) => !['frequency', 'thruplay', 'reach'].includes(c.key));

const th = {
  padding: '10px 9px', fontSize: 12, fontWeight: 600, color: C.muted, textAlign: 'right',
  borderBottom: `1px solid ${C.line}`, whiteSpace: 'nowrap', background: '#FAFBFF', cursor: 'pointer', userSelect: 'none',
};
const td = { padding: '10px 9px', fontSize: 13, color: C.ink2, textAlign: 'right', borderBottom: `1px solid ${C.hair}`, whiteSpace: 'nowrap', verticalAlign: 'middle' };

function SortTh({ col, sort, setSort }) {
  const on = sort.key === col.key;
  return (
    <th style={{ ...th, color: on ? C.ink : C.muted }}
      onClick={() => setSort({ key: col.key, dir: on && sort.dir === 'desc' ? 'asc' : 'desc' })}>
      {col.label}{on ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  );
}

function sortRows(rows, sort) {
  const k = sort.key;
  return [...rows].sort((a, b) => {
    const va = a[k] ?? -Infinity; const vb = b[k] ?? -Infinity;
    return sort.dir === 'desc' ? vb - va : va - vb;
  });
}

function MetricCells({ r, cols = COLS, bold }) {
  return cols.map((c) => (
    <td key={c.key} style={{ ...td, fontWeight: bold || c.strong ? 600 : 400, color: bold || c.strong ? C.ink : C.ink2 }}>
      {c.bar ? <RateBar value={r[c.key]} /> : c.fmt(r[c.key])}
    </td>
  ));
}

function Thumb({ ad, width = 160, onOpen }) {
  const aspect = ad.thumbAspect || (ad.creativeType === 'carousel' ? 0.8 : 1);
  return (
    <button onClick={(e) => { e.stopPropagation(); onOpen(ad); }} title="Open creative"
      style={{
        position: 'relative', width, height: Math.round(width / aspect), padding: 0, border: `1px solid ${C.line}`,
        borderRadius: 10, overflow: 'hidden', background: C.hair, cursor: 'zoom-in', flexShrink: 0, display: 'block',
      }}>
      {ad.thumbnail
        ? <img src={ad.thumbnail} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        : <span style={{ fontSize: 11, color: C.faint }}>No preview</span>}
      {width >= 100 && <span style={{
        position: 'absolute', left: 8, bottom: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
        background: 'rgba(17,24,39,0.72)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 999,
      }}>
        {ad.creativeType === 'video' ? <><Play size={11} fill="#fff" /> Video</> : ad.creativeType === 'carousel' ? 'Carousel' : 'Image'}
      </span>}
    </button>
  );
}

function DestBadge({ d }) {
  if (!d?.url) return null;
  return d.isShort
    ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#FFF4ED', color: '#B4461B' }}>Short link · {d.host}</span>
    : <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: C.accentSoft, color: C.accent }}>Direct · {d.host}</span>;
}

function Destination({ ad, compact }) {
  const d = ad.destination || {};
  if (!d.url) return <span style={{ color: C.bad, fontSize: 12 }}>{d.error || 'No destination URL'}</span>;
  const flags = urlFlags(ad);
  const finalNoQuery = (d.finalUrl || '').split('?')[0];
  return (
    <div style={{ textAlign: 'left', fontSize: 12, color: C.ink2, lineHeight: 1.5, ...wrap, minWidth: compact ? 0 : 280 }}>
      <div style={{ marginBottom: 4 }}><DestBadge d={d} /></div>
      <a href={d.url} target="_blank" rel="noreferrer" style={{ color: C.ink, fontWeight: 600, textDecoration: 'none', ...wrap }}>{d.url}</a>
      {d.isShort && d.finalUrl && (
        <div style={{ color: C.muted }}>→ {finalNoQuery}</div>
      )}
      {d.isShort && !d.finalUrl && <div style={{ color: C.bad }}>Short link could not be resolved</div>}
      {Object.keys(d.utm || {}).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {Object.entries(d.utm).map(([k, v]) => (
            <span key={k} style={{ fontSize: 11, background: C.hair, color: C.ink2, borderRadius: 6, padding: '1px 6px', ...wrap }}>
              <b style={{ color: C.muted, fontWeight: 600 }}>{k.replace(/^utm_/, '')}</b> {v.replace(/​/g, '⟨ZWSP⟩')}
            </span>
          ))}
        </div>
      )}
      {d.urlTags && <div style={{ color: C.muted, marginTop: 2 }}>URL parameters: {d.urlTags}</div>}
      {d.cards?.length > 0 && (
        <div style={{ color: C.muted, marginTop: 4 }}>
          Card links: {d.cards.map((c) => c.url).join(' · ')}
        </div>
      )}
      {d.source === 'page_post' && <div style={{ color: C.faint, marginTop: 2 }}>Read from the existing page post used as the ad</div>}
      {flags.map((f) => (
        <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.warn, marginTop: 4, fontWeight: 600 }}>
          <AlertTriangle size={12} /> {f}
        </div>
      ))}
    </div>
  );
}

function NameBlock({ name, id, idLabel, sub }) {
  return (
    <div style={{ minWidth: 0, textAlign: 'left' }}>
      <div style={{ fontWeight: 600, color: C.ink, ...wrap }}>{name}</div>
      <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>{idLabel} {id}</div>
      {sub}
    </div>
  );
}

function CampaignTable({ campaigns, ads, totals, onOpen }) {
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const [open, setOpen] = useState({});
  const rows = useMemo(() => sortRows(campaigns, sort), [campaigns, sort]);
  const adsBy = useMemo(() => {
    const m = {};
    for (const a of ads) (m[a.campaignId] = m[a.campaignId] || []).push(a);
    return m;
  }, [ads]);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontVariantNumeric: 'tabular-nums' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left', cursor: 'default', minWidth: 300 }}>Campaign</th>
            {COLS.map((c) => <SortTh key={c.key} col={c} sort={sort} setSort={setSort} />)}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const isOpen = !!open[c.id];
            const list = adsBy[c.id] || [];
            return (
              <React.Fragment key={c.id}>
                <tr style={{ cursor: 'pointer', background: isOpen ? '#FAFBFF' : undefined }}
                  onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}>
                  <td style={{ ...td, textAlign: 'left', ...wrap, maxWidth: 380 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <span style={{ marginTop: 2 }}>{isOpen ? <ChevronDown size={14} color={C.muted} /> : <ChevronRight size={14} color={C.muted} />}</span>
                      <NameBlock name={c.name} id={c.id} idLabel="Campaign ID"
                        sub={(
                          <div style={{ fontSize: 11, color: C.faint, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Badge d={statusOf(c)} />
                            <span>{format(c.name)} · {list.length} ad{list.length === 1 ? '' : 's'} · {c.startTime ? day(isoTZ(new Date(c.startTime))) : '—'} – {c.stopTime ? day(isoTZ(new Date(c.stopTime))) : 'no end date'}</span>
                          </div>
                        )} />
                    </div>
                  </td>
                  <MetricCells r={c} />
                </tr>
                {isOpen && list.map((a) => (
                  <tr key={a.id} style={{ background: '#FCFDFF' }}>
                    <td style={{ ...td, textAlign: 'left', paddingLeft: 30, ...wrap, maxWidth: 380 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Thumb ad={a} width={64} onOpen={onOpen} />
                        <NameBlock name={a.name} id={a.id} idLabel="Ad ID" />
                      </div>
                    </td>
                    <MetricCells r={a} />
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {totals && (
            <tr style={{ background: C.accentSoft }}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: C.ink, borderBottom: 'none' }}>
                Total · {campaigns.length} campaigns
                <div style={{ fontSize: 11, fontWeight: 400, color: C.muted }}>Reach and frequency deduplicated (account level)</div>
              </td>
              {COLS.map((c) => (
                <td key={c.key} style={{ ...td, fontWeight: 700, color: C.ink, borderBottom: 'none' }}>
                  {c.bar ? <RateBar value={totals[c.key]} /> : c.fmt(totals[c.key])}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AdTable({ ads, onOpen }) {
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const rows = useMemo(() => sortRows(ads, sort), [ads, sort]);
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontVariantNumeric: 'tabular-nums' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left', cursor: 'default', minWidth: 390 }}>Ad</th>
            <th style={{ ...th, textAlign: 'left', cursor: 'default', minWidth: 260 }}>Destination URL</th>
            {AD_COLS.map((c) => <SortTh key={c.key} col={c} sort={sort} setSort={setSort} />)}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id}>
              <td style={{ ...td, textAlign: 'left', verticalAlign: 'top', ...wrap }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Thumb ad={a} width={160} onOpen={onOpen} />
                  <div style={{ minWidth: 0, maxWidth: 220 }}>
                    <NameBlock name={a.name} id={a.id} idLabel="Ad ID"
                      sub={<div style={{ fontSize: 11, color: C.faint, marginTop: 6, ...wrap }}>Campaign: {a.campaignName}<br />Campaign ID {a.campaignId}</div>} />
                    <button onClick={() => onOpen(a)} style={{
                      marginTop: 8, fontSize: 12, fontWeight: 600, color: C.accent, background: C.accentSoft, border: 'none',
                      borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontFamily: FONT,
                    }}>View creative</button>
                  </div>
                </div>
              </td>
              <td style={{ ...td, textAlign: 'left', verticalAlign: 'top', maxWidth: 300 }}><Destination ad={a} compact /></td>
              <MetricCells r={a} cols={AD_COLS} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------- lightbox */

function Lightbox({ ad, brandId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const [idx, setIdx] = useState(0);
  const [mode, setMode] = useState('media'); // media | preview
  const [previewFmt, setPreviewFmt] = useState('MOBILE_FEED_STANDARD');

  useEffect(() => {
    let alive = true;
    setDetail(null); setErr(null); setIdx(0); setMode('media');
    fetch(`${API_BASE}/api/canon-report/coral/ad/${ad.id}`, { credentials: 'include', headers: { 'X-Brand-ID': brandId } })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `Could not load creative (HTTP ${r.status})`);
        if (alive) {
          setDetail(j);
          // Fallback to Meta's preview iframe when the media itself isn't available.
          const noMedia = (j.type === 'video' && !j.video?.source) || (j.type === 'carousel' && !j.cards?.length) || (j.type === 'image' && !j.image) || !j.type;
          if (noMedia) setMode('preview');
        }
      })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [ad.id, brandId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (detail?.cards?.length) {
        if (e.key === 'ArrowRight') setIdx((i) => Math.min(detail.cards.length - 1, i + 1));
        if (e.key === 'ArrowLeft') setIdx((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail, onClose]);

  const cards = detail?.cards || [];
  const card = cards[idx];
  const previewSrc = detail?.previews?.[previewFmt] || Object.values(detail?.previews || {})[0];
  const navBtn = (side) => ({
    position: 'absolute', top: '50%', [side]: 10, transform: 'translateY(-50%)', width: 40, height: 40, borderRadius: 999,
    border: 'none', background: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });
  const metric = (k, v) => (
    <div style={{ padding: '8px 0', borderBottom: `1px solid ${C.hair}`, display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
      <span style={{ color: C.muted }}>{k}</span><b style={{ color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{v}</b>
    </div>
  );

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.72)', zIndex: 2000, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: FONT,
    }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Ad creative" style={{
        background: C.card, borderRadius: 16, width: 'min(1180px, 100%)', maxHeight: '92vh', display: 'flex', overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)', flexWrap: 'wrap',
      }}>
        {/* media */}
        <div style={{ flex: '1 1 520px', background: '#0B0F19', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 420, position: 'relative' }}>
          {!detail && !err && <div style={{ color: '#9CA3AF', fontSize: 14 }}>Loading creative from Meta…</div>}
          {err && <div style={{ color: '#FCA5A5', fontSize: 14, padding: 24 }}>{err}</div>}
          {detail && mode === 'media' && detail.type === 'video' && detail.video?.source && (
            <video key={detail.video.source} src={detail.video.source} poster={detail.video.picture || undefined}
              controls autoPlay muted playsInline style={{ maxHeight: '80vh', maxWidth: '100%', display: 'block' }} />
          )}
          {detail && mode === 'media' && detail.type === 'carousel' && card && (
            <div style={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '16px 0' }}>
              <img src={card.image} alt="" referrerPolicy="no-referrer" style={{ maxHeight: '68vh', maxWidth: '100%', display: 'block', borderRadius: 6 }} />
              {idx > 0 && <button aria-label="Previous card" onClick={() => setIdx(idx - 1)} style={navBtn('left')}><ChevronLeft size={20} /></button>}
              {idx < cards.length - 1 && <button aria-label="Next card" onClick={() => setIdx(idx + 1)} style={navBtn('right')}><ChevronRight size={20} /></button>}
              <div style={{ color: '#E5E7EB', fontSize: 13, marginTop: 10, textAlign: 'center', padding: '0 16px', ...wrap }}>
                <div style={{ fontWeight: 700 }}>Card {card.index} of {cards.length} · {card.headline || 'No headline set'}</div>
                {card.description && <div style={{ color: '#9CA3AF' }}>{card.description}</div>}
                {card.link && <div style={{ color: '#9CA3AF', marginTop: 2 }}>{card.link}{card.finalUrl && card.finalUrl !== card.link ? ` → ${card.finalUrl.split('?')[0]}` : ''}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {cards.map((c, i) => (
                  <button key={c.index} onClick={() => setIdx(i)} aria-label={`Card ${c.index}`} style={{
                    width: 44, height: 44, padding: 0, borderRadius: 6, overflow: 'hidden', cursor: 'pointer',
                    border: i === idx ? `2px solid ${C.accent}` : '2px solid transparent', background: '#1F2937',
                  }}><img src={c.image} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></button>
                ))}
              </div>
            </div>
          )}
          {detail && mode === 'media' && detail.type === 'image' && detail.image && (
            <img src={detail.image.url} alt="" referrerPolicy="no-referrer" style={{ maxHeight: '80vh', maxWidth: '100%' }} />
          )}
          {detail && mode === 'preview' && (
            previewSrc
              ? <iframe title="Meta ad preview" src={previewSrc} style={{ width: 340, height: '78vh', maxHeight: 640, border: 'none', background: '#fff', borderRadius: 8 }} allow="autoplay" />
              : <div style={{ color: '#FCA5A5', fontSize: 14, padding: 24 }}>Meta preview not available{detail.previewErrors ? `: ${Object.values(detail.previewErrors).join('; ')}` : ''}</div>
          )}
          {detail && (
            <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 6 }}>
              {['media', 'preview'].map((m) => (
                <button key={m} onClick={() => setMode(m)} style={{
                  fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: FONT,
                  background: mode === m ? '#fff' : 'rgba(255,255,255,0.15)', color: mode === m ? C.ink : '#E5E7EB',
                }}>{m === 'media' ? (detail.type === 'video' ? 'Video' : detail.type === 'carousel' ? 'Cards' : 'Media') : 'Meta preview'}</button>
              ))}
              {mode === 'preview' && Object.keys(detail.previews || {}).length > 1 && (
                <select value={previewFmt} onChange={(e) => setPreviewFmt(e.target.value)} style={{ fontSize: 12, borderRadius: 999, padding: '4px 8px', fontFamily: FONT }}>
                  <option value="MOBILE_FEED_STANDARD">Facebook feed</option>
                  <option value="INSTAGRAM_STANDARD">Instagram feed</option>
                </select>
              )}
            </div>
          )}
          {detail?.videoError && mode === 'media' && (
            <div style={{ position: 'absolute', bottom: 12, left: 12, right: 12, color: '#FCD34D', fontSize: 12 }}>Video source unavailable: {detail.videoError}</div>
          )}
        </div>

        {/* details */}
        <div style={{ flex: '1 1 340px', maxWidth: 440, padding: 22, overflowY: 'auto', maxHeight: '92vh', position: 'relative' }}>
          <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 12, right: 12, border: 'none', background: C.hair, borderRadius: 999, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={16} />
          </button>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{ad.creativeType === 'video' ? 'Video ad' : ad.creativeType === 'carousel' ? 'Carousel ad' : 'Ad'}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.ink, paddingRight: 36, ...wrap }}>{ad.name}</div>
          <div style={{ fontSize: 11, color: C.faint, marginTop: 2 }}>Ad ID {ad.id}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 8, ...wrap }}>Campaign: {ad.campaignName}<br /><span style={{ color: C.faint }}>Campaign ID {ad.campaignId}</span></div>

          <div style={{ marginTop: 14 }}>
            {metric('Spend', tl(ad.spend))}
            {metric('Link clicks', nf(ad.linkClicks))}
            {metric('Landing page views', nf(ad.lpv))}
            {metric('CPC (link)', tl(ad.cpc))}
            {metric('Cost / LPV', tl(ad.costPerLpv))}
            {metric('LPV / click', pc(ad.lpvRate))}
            {metric('Impressions · CTR', `${nf(ad.impressions)} · ${pc(ad.ctr, 2)}`)}
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.ink, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}><Link2 size={13} /> Destination URL</div>
            <Destination ad={detail ? { ...ad, destination: detail.destination || ad.destination } : ad} compact />
          </div>

          {detail?.headline && <div style={{ marginTop: 16, fontSize: 13 }}><span style={{ color: C.muted }}>Headline:</span> <b>{detail.headline}</b></div>}
          {detail?.primaryText && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Primary text (as written in the ad)</div>
              <div style={{ fontSize: 12, color: C.ink2, whiteSpace: 'pre-wrap', background: C.hair, borderRadius: 8, padding: 10, maxHeight: 180, overflowY: 'auto' }}>{detail.primaryText}</div>
            </div>
          )}
          {detail?.video?.permalink && (
            <a href={detail.video.permalink} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 12, fontSize: 12, color: C.accent }}>
              Open video on Facebook <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- chart */

function DailyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.ink2, boxShadow: '0 4px 12px rgba(16,24,40,0.08)' }}>
      <div style={{ fontWeight: 700, color: C.ink, marginBottom: 6 }}>{day(label)}</div>
      <Row k="Link clicks" v={nf(d.linkClicks)} dot={C.accent} />
      <Row k="Landing page views" v={nf(d.lpv)} dot={C.orange} />
      <Row k="LPV / click" v={pc(d.lpvRate)} />
      <Row k="Spend" v={tl(d.spend)} />
      <Row k="Cost / LPV" v={tl(d.costPerLpv)} />
    </div>
  );
}
const Row = ({ k, v, dot }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {dot && <span style={{ width: 8, height: 8, borderRadius: 2, background: dot }} />}{k}
    </span>
    <b style={{ color: C.ink, fontVariantNumeric: 'tabular-nums' }}>{v}</b>
  </div>
);

function FormatCompare({ campaigns }) {
  const groups = useMemo(() => {
    const m = {};
    for (const c of campaigns) {
      const f = format(c.name);
      const g = m[f] || (m[f] = { format: f, spend: 0, linkClicks: 0, lpv: 0, n: 0 });
      g.spend += c.spend; g.linkClicks += c.linkClicks; g.lpv += c.lpv; g.n += 1;
    }
    return Object.values(m).sort((a, b) => b.spend - a.spend);
  }, [campaigns]);
  const totalSpend = groups.reduce((s, g) => s + g.spend, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {groups.map((g) => (
        <div key={g.format} style={{ border: `1px solid ${C.hair}`, borderRadius: 10, padding: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, color: C.ink }}>{g.format} <span style={{ fontWeight: 400, color: C.faint, fontSize: 12 }}>· {g.n} campaign{g.n === 1 ? '' : 's'}</span></span>
            <span style={{ fontSize: 12, color: C.muted }}>{pc(totalSpend ? (g.spend / totalSpend) * 100 : 0, 0)} of spend</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 8, fontVariantNumeric: 'tabular-nums' }}>
            <MiniStat k="Spend" v={tl(g.spend, 0)} />
            <MiniStat k="Cost / LPV" v={tl(g.lpv ? g.spend / g.lpv : null)} />
            <MiniStat k="CPC" v={tl(g.linkClicks ? g.spend / g.linkClicks : null)} />
            <MiniStat k="LPV / click" v={pc(g.linkClicks ? (g.lpv / g.linkClicks) * 100 : null)} />
          </div>
        </div>
      ))}
    </div>
  );
}
const MiniStat = ({ k, v }) => (
  <div>
    <div style={{ fontSize: 11, color: C.muted }}>{k}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: C.ink, whiteSpace: 'nowrap' }}>{v}</div>
  </div>
);

/* ------------------------------------------------------------------ page */

export default function CanonCoralReport() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || CANON_BRAND_ID;

  const [preset, setPreset] = useState('lifetime');
  const [custom, setCustom] = useState(['2026-09-01', isoTZ(new Date())]);
  const [range, setRange] = useState(null); // null = campaign lifetime
  const [tab, setTab] = useState('campaigns');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openAd, setOpenAd] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const q = range ? `?since=${range[0]}&until=${range[1]}` : '';
      const res = await fetch(`${API_BASE}/api/canon-report/coral${q}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `Could not load the report (HTTP ${res.status})`);
      setData(json);
    } catch (e) {
      setError(e.message || 'Could not load the report');
    } finally {
      setLoading(false);
    }
  }, [range, brandId]);

  useEffect(() => { load(); }, [load]);

  const choose = (k) => {
    setPreset(k);
    if (k === 'custom') { setRange([...custom]); return; }
    setRange(presetRange(k));
  };

  const t = data?.totals;
  const campaigns = data?.campaigns || [];
  const ads = data?.ads || [];
  const daily = data?.daily || [];
  const ranked = useMemo(() => [...campaigns].filter((c) => c.costPerLpv != null).sort((a, b) => a.costPerLpv - b.costPerLpv), [campaigns]);
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];
  const peak = useMemo(() => [...daily].sort((a, b) => b.lpv - a.lpv)[0], [daily]);
  const activeDays = daily.filter((d) => d.spend > 0).length;
  const shortAds = ads.filter((a) => a.destination?.isShort).length;

  const fetchedAt = data?.fetchedAt
    ? new Date(data.fetchedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'medium', timeZone: TZ }) + ' (TRT)'
    : null;

  if (selectedBrand && selectedBrand.id !== CANON_BRAND_ID) {
    return <div style={{ padding: 32, fontFamily: FONT, color: C.muted }}>This report is only available for the Canon brand.</div>;
  }

  return (
    <div style={{ background: C.ground, minHeight: '100%', padding: '24px 28px 48px', fontFamily: FONT, color: C.ink }}>
      <style>{'@keyframes coralspin{to{transform:rotate(360deg)}} .coral-kpi{grid-template-columns:repeat(8,minmax(0,1fr))} @media (max-width:1500px){.coral-kpi{grid-template-columns:repeat(4,minmax(0,1fr))}} @media (max-width:640px){.coral-kpi{grid-template-columns:repeat(2,minmax(0,1fr))}} .coral-main{display:grid;grid-template-columns:minmax(0,2.2fr) minmax(280px,1fr);gap:16px;margin-bottom:16px} @media (max-width:1100px){.coral-main{grid-template-columns:minmax(0,1fr)}}'}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, marginBottom: 4 }}>Canon · Meta Ads · World Unseen</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>Coral Campaign Report</h1>
          <div style={{ fontSize: 13, color: C.muted, marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: C.good, fontWeight: 600 }}>
              <Radio size={13} /> Live · Meta Marketing API
            </span>
            <span>·</span>
            <span>{data?.account?.name || 'Jellybon | Canon'}</span>
            <span>·</span>
            <span>Campaigns with “Coral” in the name</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ textAlign: 'right', fontSize: 12, color: C.muted }}>
            <div>Last updated</div>
            <div style={{ fontWeight: 600, color: C.ink2 }}>{fetchedAt || '—'}</div>
          </div>
          <button onClick={load} disabled={loading} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10,
            border: 'none', background: C.accent, color: '#fff', fontWeight: 600, fontSize: 13,
            cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: FONT,
          }}>
            <RefreshCw size={14} style={{ animation: loading ? 'coralspin 1s linear infinite' : 'none' }} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Date range */}
      <Card style={{ padding: '10px 12px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {PRESETS.map((p) => (
          <button key={p.key} onClick={() => choose(p.key)} style={{
            padding: '7px 12px', borderRadius: 8, fontSize: 13, fontFamily: FONT, cursor: 'pointer',
            border: `1px solid ${preset === p.key ? C.accent : C.line}`,
            background: preset === p.key ? C.accentSoft : C.card,
            color: preset === p.key ? C.accent : C.ink2, fontWeight: preset === p.key ? 600 : 500,
          }}>{p.label}</button>
        ))}
        {preset === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 4 }}>
            <input type="date" lang="en" value={custom[0]} max={custom[1]} onChange={(e) => setCustom([e.target.value, custom[1]])}
              style={{ padding: '6px 8px', border: `1px solid ${C.line}`, borderRadius: 8, fontFamily: FONT, fontSize: 13 }} />
            <span style={{ color: C.faint }}>–</span>
            <input type="date" lang="en" value={custom[1]} min={custom[0]} onChange={(e) => setCustom([custom[0], e.target.value])}
              style={{ padding: '6px 8px', border: `1px solid ${C.line}`, borderRadius: 8, fontFamily: FONT, fontSize: 13 }} />
            <button onClick={() => setRange([...custom])} style={{
              padding: '7px 12px', borderRadius: 8, border: 'none', background: C.ink, color: '#fff', fontSize: 13, fontFamily: FONT, cursor: 'pointer',
            }}>Apply</button>
          </div>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 13, color: C.muted }}>
          {data?.range ? <>Period: <b style={{ color: C.ink }}>{day(data.range.since)} – {day(data.range.until)}, {data.range.until.slice(0, 4)}</b> · {activeDays} delivery days</> : ''}
        </div>
      </Card>

      {error && (
        <Card style={{ padding: '14px 16px', marginBottom: 16, background: C.badSoft, borderColor: '#FECACA', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} color={C.bad} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: C.bad }}>
            <b>Could not fetch data from Meta.</b> No figures are shown for this reason.<div style={{ marginTop: 4 }}>{error}</div>
          </div>
        </Card>
      )}
      {data?.warnings?.length > 0 && (
        <Card style={{ padding: '12px 16px', marginBottom: 16, background: C.warnSoft, borderColor: '#FDE68A', fontSize: 13, color: C.warn }}>
          {data.warnings.map((w) => <div key={w}>⚠ {w}</div>)}
        </Card>
      )}

      {!data && loading && <Card style={{ padding: 40, textAlign: 'center', color: C.muted }}>Fetching live data from Meta…</Card>}

      {data && !error && t && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .2s' }}>
          {/* KPIs */}
          <div className="coral-kpi" style={{ display: 'grid', gap: 12, marginBottom: 12 }}>
            <Kpi label="Spend" value={tl(t.spend)} sub={`${t.campaignCount} campaigns · ${t.adCount} ads`} />
            <Kpi label="Results (landing page views)" value={nf(t.results)} sub="Optimisation goal" accent />
            <Kpi label="Link clicks" value={nf(t.linkClicks)} sub={`CTR ${pc(t.ctr, 2)}`} />
            <Kpi label="Landing page views" value={nf(t.lpv)} sub={`${pc(t.lpvRate)} of link clicks`} />
            <Kpi label="Reach (deduplicated)" value={nf(t.reach)} sub={`Frequency ${nf(t.frequency, 2)}`} />
            <Kpi label="Impressions" value={nf(t.impressions)} sub={`CPM ${tl(t.cpm)}`} />
            <Kpi label="CPC (link)" value={tl(t.cpc)} sub="Spend ÷ link clicks" />
            <Kpi label="Cost per LPV" value={tl(t.costPerLpv)} sub="Cost per result" accent />
          </div>
          <Card style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 16, overflow: 'hidden' }}>
            <Mini label="3-second video plays" value={nf(t.videoViews3s)} />
            <Mini label="ThruPlays" value={nf(t.thruplay)} />
            <Mini label="Video plays at 100%" value={nf(t.videoP100)} />
            <Mini label="Reactions" value={nf(t.reactions)} />
            <Mini label="Reach (sum of rows)" value={nf(t.reachRowSum)} />
            <Mini label="Audience overlap" value={pc(t.reachRowSum ? (1 - t.reach / t.reachRowSum) * 100 : null)} />
            <Mini label="Avg. daily spend" value={tl(activeDays ? t.spend / activeDays : null, 0)} />
            <Mini label="Ads using short links" value={`${shortAds} of ${ads.length}`} />
          </Card>

          {/* Chart + side panel */}
          <div className="coral-main">
            <Card style={{ padding: '16px 16px 8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>Daily link clicks vs landing page views</div>
                  <div style={{ fontSize: 12, color: C.muted }}>The gap = people who clicked but left before the page loaded</div>
                </div>
                {peak && <div style={{ fontSize: 12, color: C.muted }}>Peak LPV: <b style={{ color: C.ink }}>{day(peak.date)} · {nf(peak.lpv)}</b></div>}
              </div>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily} barGap={2} barCategoryGap="22%" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={C.hair} />
                    <XAxis dataKey="date" tickFormatter={day} tick={{ fontSize: 11, fill: C.muted }} axisLine={{ stroke: C.line }} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
                    <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} width={44}
                      tickFormatter={(v) => (v >= 1000 ? `${nf(v / 1000, v % 1000 ? 1 : 0)}K` : v)} />
                    <Tooltip content={<DailyTooltip />} cursor={{ fill: 'rgba(67,106,235,0.06)' }} />
                    <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12, color: C.ink2 }} />
                    <Bar dataKey="linkClicks" name="Link clicks" fill={C.accent} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                    <Bar dataKey="lpv" name="Landing page views" fill={C.orange} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
            <Card style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Format comparison</div>
              <FormatCompare campaigns={campaigns} />
              {best && worst && best.id !== worst.id && (
                <div style={{ fontSize: 12, color: C.ink2, lineHeight: 1.55, borderTop: `1px solid ${C.hair}`, paddingTop: 12, ...wrap }}>
                  Cheapest LPV: <b>{best.name}</b> ({tl(best.costPerLpv)}). Most expensive: <b>{worst.name}</b> ({tl(worst.costPerLpv)}) —
                  {' '}{nf(worst.costPerLpv / best.costPerLpv, 1)}× difference.
                </div>
              )}
            </Card>
          </div>

          {/* Tables */}
          <Card style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '10px 12px', borderBottom: `1px solid ${C.line}` }}>
              {[['campaigns', `Campaigns (${campaigns.length})`], ['ads', `Ads (${ads.length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} style={{
                  padding: '7px 12px', borderRadius: 8, border: 'none', fontFamily: FONT, fontSize: 13, cursor: 'pointer',
                  background: tab === k ? C.accentSoft : 'transparent', color: tab === k ? C.accent : C.muted, fontWeight: 600,
                }}>{l}</button>
              ))}
              <span style={{ marginLeft: 'auto', fontSize: 12, color: C.faint }}>
                {tab === 'campaigns' ? 'Click a row to show its ads · click a header to sort' : 'Click a creative to view it large · click a header to sort'}
              </span>
            </div>
            {tab === 'campaigns'
              ? <CampaignTable campaigns={campaigns} ads={ads} totals={t} onOpen={setOpenAd} />
              : <AdTable ads={ads} onOpen={setOpenAd} />}
          </Card>

          <div style={{ fontSize: 12, color: C.faint, marginTop: 12, lineHeight: 1.6 }}>
            Source: Meta Marketing API {data.apiVersion}, fetched live on every load ({nf(data.tookMs / 1000, 1)} s).
            Results = landing page views (the campaigns' optimisation goal). Link clicks = inline_link_clicks.
            Total reach is Meta's deduplicated account-level figure; summing campaign rows counts people more than once.
            Short links are followed to show the final URL and its UTM parameters. Currency {data.account?.currency}, time zone {data.account?.timezone}.
          </div>
        </div>
      )}

      {openAd && <Lightbox ad={openAd} brandId={brandId} onClose={() => setOpenAd(null)} />}
    </div>
  );
}
